import { env } from "../env";

/**
 * Whether Google/Microsoft sign-in should open a popup instead of a full-page redirect.
 *
 * Only ever true in a dev server, and only when the dev script asks for it. The redirect flow
 * needs pending sign-in state in the auth domain's storage; when the app runs on `localhost` and
 * the auth domain is a deployed site, Chrome partitions that storage away from the opener, so the
 * state is gone on the way back. `getRedirectResult` then resolves `null` — indistinguishable from
 * "no sign-in was pending" — and the user silently lands back on the login form. A popup loads the
 * same handler as a first-party top-level context and hands the credential back by `postMessage`,
 * which partitioning does not touch.
 *
 * The `import.meta.env.DEV` half is not redundant: `env` reads the runtime-injected `window.__ENV__`
 * before the build-time values, so without it a deployed runtime config could flip the flow.
 */
export function shouldUseOAuthPopup(): boolean {
  if (import.meta.env?.DEV !== true) return false;
  return env.VITE_AUTH_OAUTH_POPUP === "true";
}

/** Closing the popup, or opening a second one, is a cancel — not a sign-in failure to report. */
export function isPopupCancelledError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("code" in error)) return false;
  const code = (error as { code?: string }).code;
  return (
    code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request" || code === "auth/user-cancelled"
  );
}
